import React, { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { KeyRound, ChevronRight, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/auth";
import { useAuth } from "../lib/AuthContext";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <main className="min-h-screen bg-[#0E2452] pt-32 pb-20 px-6 md:px-12" data-testid="reset-invalid">
        <div className="max-w-md mx-auto rounded-2xl border border-[#FF5A00]/40 bg-[#0B1D44] p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#FF5A00]/15 border-2 border-[#FF5A00]/50 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-[#FF5A00]" />
          </div>
          <h1 className="font-display font-bold text-white text-2xl uppercase mb-3">Link inválido</h1>
          <p className="text-white/70 font-body text-sm leading-relaxed mb-5">
            Este link de reposição não parece ser válido. Peça um novo através da página "Esqueci-me da palavra-passe".
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65100] text-white font-display font-bold uppercase text-xs tracking-widest px-5 py-3 rounded-full transition-all"
          >
            Pedir novo link
          </Link>
        </div>
      </main>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (password.length < 6) {
      toast.error("A palavra-passe deve ter pelo menos 6 caracteres.");
      return;
    }
    if (password !== confirm) {
      toast.error("As palavras-passe não coincidem.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/reset-password`,
        { token, password },
        { withCredentials: true }
      );
      setUser({ user_id: data.user_id, email: data.email, name: data.name, picture: data.picture, telefone: data.telefone });
      toast.success("Palavra-passe atualizada!");
      navigate("/minha-conta", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Não foi possível repor. O link pode estar expirado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0E2452] pt-32 pb-20 px-6 md:px-12" data-testid="reset-password-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="section-label mb-2 justify-center">// Nova Palavra-passe</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase text-white leading-tight">
            Definir <span className="text-[#FF5A00]">nova senha</span>
          </h1>
          <p className="text-white/60 text-sm font-body mt-3">
            Escolha uma palavra-passe segura. Vamos guardá-la de forma cifrada.
          </p>
        </div>

        <div className="rounded-2xl border border-[#2F568F] bg-[#0B1D44] p-6 md:p-8">
          <form onSubmit={submit} className="flex flex-col gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-display block mb-1">Nova palavra-passe</label>
              <div className="relative">
                <KeyRound className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  data-testid="reset-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  autoComplete="new-password"
                  minLength={6}
                  className="w-full bg-[#0E2452] border border-[#2F568F] text-white pl-11 pr-4 py-3 rounded-xl focus:outline-none focus:border-[#FF5A00]"
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-display block mb-1">Confirmar palavra-passe</label>
              <input
                data-testid="reset-password-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repita a nova palavra-passe"
                autoComplete="new-password"
                minLength={6}
                className="w-full bg-[#0E2452] border border-[#2F568F] text-white px-4 py-3 rounded-xl focus:outline-none focus:border-[#FF5A00]"
                required
              />
            </div>
            <button
              data-testid="reset-submit"
              type="submit"
              disabled={loading}
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E65100] text-white font-display font-bold uppercase tracking-wider py-3 rounded-full transition-all disabled:opacity-50 active:scale-[0.98]"
            >
              {loading ? "A guardar..." : "Guardar nova palavra-passe"}
              <CheckCircle2 className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default ResetPasswordPage;
